import React, { FC, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import {
	BUTTON_TYPES,
	EvButton,
	EvIssueDetails,
	IIssueElement,
	IIssueTag,
	IssueDetailsHelper,
	ScreenshotDetails
} from '@evinced-private/ui-common';

import { useGetScanIssue } from 'src/hooks/queries/useGetScanIssue';
import { ScreenshotService } from 'src/services/ScreenshotService';

import { RoutesHelper } from '../../helpers/RoutesHelper';
import { getBaseURL } from '../../helpers/UrlNormalizingHelper';
import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { SiteScannerUiToggles } from '../../services/LocalTogglesService';
import { logger } from '../../services/Logger';
import { CopyToClipboard } from '../common/copy-to-clipboard/CopyToClipboard';
import { TicketsSystems } from '../tickets-systems/TicketsSystems';

interface IIssueDetailsProps {
	issueId: string;
	scanId?: string;
	setError: (e: Error) => void;
	setTitle?: (title: string) => void;
	isPage?: boolean;
}

export const IssueDetailsBlock: FC<IIssueDetailsProps> = ({
	issueId,
	scanId,
	setError,
	setTitle,
	isPage
}: IIssueDetailsProps) => {
	const { propertyId } = useParams<{ propertyId: string }>();

	const openIssueInNewTab = useCallback((): void => {
		window.open(RoutesHelper.getIssueDetailsPath(propertyId, scanId, issueId), '_blank');
	}, [propertyId, scanId, issueId]);

	const renderCopyToClipboardButton = useCallback(
		(textToCopy: string) => (
			<CopyToClipboard renderJustIcon textToCopy={textToCopy} iconTitle="Copy Selector" />
		),
		[]
	);

	const { data: issue, isLoading, error } = useGetScanIssue(scanId, issueId);

	const { getToggle, configuration } = useConfiguration();
	const shouldShowChatbotLink = getToggle(SiteScannerUiToggles.ENABLE_CHATBOT_LINK);
	const slackChatbotLinkConfig = configuration?.GLOBAL?.CONFIG?.SLACK_CHATBOT;
	const msTeamsChatbotLinkConfig = configuration?.GLOBAL?.CONFIG?.MS_TEAMS_CHATBOT;
	const chatbotLinkConfig = slackChatbotLinkConfig || msTeamsChatbotLinkConfig;

	useEffect(() => {
		if (issue && setTitle) {
			setTitle(issue?.type?.name);
		}
	}, [issue, setTitle]);

	useEffect(() => {
		if (error) {
			setError(error as Error);
			logger.warn('An error occurred while fetching single issue', error);
		}
	}, [error, setError]);

	const {
		description,
		type,
		severity,
		pageScreenshot,
		elements,
		summary,
		note,
		knowledgeBaseLink,
		customKnowledgeBaseLinks = [],
		tags
	} = issue || {};
	const { pageUrl } = elements?.[0] ?? {};
	const urlToCopy = `${getBaseURL()}${RoutesHelper.getIssueDetailsPath(
		propertyId,
		scanId,
		issueId
	)}`;

	const topRowActions = (
		<div className="action-buttons">
			<CopyToClipboard
				textToCopy={urlToCopy}
				buttonType={BUTTON_TYPES.SECONDARY}
				dataAutomationId="copy-issue-link-btn"
			/>
			{!isPage && (
				<EvButton
					type={BUTTON_TYPES.SECONDARY}
					title="Open in a New Tab"
					onClick={openIssueInNewTab}
				>
					Open in a New Tab
				</EvButton>
			)}
			<TicketsSystems issue={issue} />
		</div>
	);

	const screenshotDetails: ScreenshotDetails = {
		pageScreenshotUrl: pageScreenshot,
		boundingBoxes: elements?.map((element) => element.boundingBox) || []
	};

	return (
		<EvIssueDetails
			issue={{
				type: type?.name,
				severity: severity?.name,
				effect: IssueDetailsHelper.getEffectString(description || ''),
				cause: IssueDetailsHelper.getCauseString(description || ''),
				howToFix: IssueDetailsHelper.getHowToString(description || ''),
				summary,
				note,
				knowledgeBaseLink,
				pageUrl,
				screenshotDetails,
				level: tags?.[0]?.description,
				pageTitle: '',
				elements: elements as IIssueElement[],
				tags: tags as IIssueTag[],
				customKnowledgeBaseLinks
			}}
			isLoading={isLoading}
			isFullPageMode={isPage}
			topRowActions={topRowActions}
			screenshotService={ScreenshotService}
			logger={logger}
			copyToClipboardButton={renderCopyToClipboardButton}
			showChatbotLink={shouldShowChatbotLink}
			chatbotLinkConfig={chatbotLinkConfig}
		/>
	);
};
